import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { useLocaleState, useTranslate, type Identifier } from "ra-core";
import { useState } from "react";

import { getSupabaseClient } from "../providers/supabase/supabase";
import { Segmented } from "./Segmented";
import { StatePill } from "./renewal";

/** Shape returned by construnario-web GET /api/crm/estadisticas. */
interface WebStats {
  empresa: string | null;
  rango: { desde: string; hasta: string };
  fichas: { carp: string; online: boolean; ok: boolean; url: string | null }[];
  catalogo: { productos: number; documentos: number; videos: number };
  resumen: {
    visitas: number;
    visitantes: number;
    producto: number;
    noticia: number;
    descarga: number;
    peticiones: number;
    contactos: number;
    impresiones: number;
  };
  diarias: { fecha: string; visitas: number }[];
  productos: TopItem[];
  noticias: TopItem[];
  descargas: TopItem[];
  peticiones: {
    total: number;
    perfiles: { perfil: string; peticiones: number; pct: number }[];
    ultimas: {
      fecha: string;
      producto: string | null;
      perfil: string | null;
      provincia: string | null;
      pais: string | null;
      respondida: boolean;
    }[];
  };
  noticiasPublicadas: {
    total: number;
    ultimas: { fecha: string; titular: string; url: string }[];
  };
}
interface TopItem {
  nombre: string;
  url: string | null;
  visitas: number;
}

type Range = "12m" | "year" | "30d";
const WEB = "https://www.construnario.com";

const rangeDates = (range: Range) => {
  const to = new Date();
  const from = new Date(to);
  if (range === "12m") from.setFullYear(from.getFullYear() - 1);
  if (range === "year") from.setMonth(0, 1);
  if (range === "30d") from.setDate(from.getDate() - 30);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { desde: iso(from), hasta: iso(to) };
};

const fetchWebStats = async (companyId: Identifier, range: Range) => {
  const { data, error } = await getSupabaseClient().functions.invoke<WebStats>(
    "web-stats",
    { body: { companyId, ...rangeDates(range) } },
  );
  if (error) {
    // Non-2xx answers carry the reason in the body: "not-linked" (company
    // without construnario.com id), "no-configurado" / not configured (shared
    // secret missing on one side), or a plain message.
    let reason = error.message;
    try {
      const body = await (
        error as { context?: { json?: () => Promise<Record<string, string>> } }
      ).context?.json?.();
      reason = body?.code || body?.error || body?.message || reason;
    } catch {
      // keep the generic message
    }
    throw new Error(reason);
  }
  return data!;
};

const nf = new Intl.NumberFormat("es-ES", { useGrouping: true });
const fmt = (n: number) =>
  String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ".");

/**
 * What the client sees in its own panel on construnario.com (visits, rankings,
 * requests, news), on the company page of the CRM. Data comes live from the
 * website through the `web-stats` edge function; nothing is copied.
 */
export const WebStatsTab = ({ companyId }: { companyId: Identifier }) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const [range, setRange] = useState<Range>("12m");
  const { data, isPending, error } = useQuery({
    queryKey: ["web-stats", companyId, range],
    queryFn: () => fetchWebStats(companyId, range),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const t = (key: string, options?: Record<string, unknown>) =>
    translate(`crm.construnario.web.${key}`, options);
  const date = (value: string) =>
    new Date(value).toLocaleDateString(locale, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });

  const live = data?.fichas.find((f) => f.online && f.ok);

  return (
    <div className="flex flex-col gap-4 pt-2">
      <div className="flex flex-wrap items-center gap-2">
        <Segmented
          label={t("range")}
          value={range}
          onChange={setRange}
          options={(["12m", "year", "30d"] as Range[]).map((r) => ({
            value: r,
            label: t(`ranges.${r}`),
          }))}
        />
        {data ? (
          <span className="text-xs text-muted-foreground">
            {date(data.rango.desde)} – {date(data.rango.hasta)}
          </span>
        ) : null}
      </div>

      {error ? (
        <p className="text-sm text-muted-foreground">
          {(() => {
            const reason = String((error as Error).message);
            if (reason === "not-linked") return t("not_linked");
            if (/no-configurado|not configured/i.test(reason))
              return t("not_configured");
            return t("error");
          })()}
        </p>
      ) : isPending || !data ? (
        <p className="text-sm text-muted-foreground">{t("loading")}</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 text-[13px]">
            <StatePill
              tone={
                live
                  ? "bg-good-tint text-good"
                  : "bg-neutral-tint text-neutral-state"
              }
            >
              {live ? t("published") : t("not_published")}
            </StatePill>
            <span className="text-muted-foreground">
              {t("catalogue", {
                products: fmt(data.catalogo.productos),
                documents: fmt(data.catalogo.documentos),
                videos: fmt(data.catalogo.videos),
              })}
            </span>
            {live?.url ? (
              <a
                href={live.url}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-auto inline-flex items-center gap-1 text-accent-text"
              >
                {t("open_page")} <ExternalLink className="size-3.5" />
              </a>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border bg-border sm:grid-cols-4">
            {(
              [
                ["visitas", data.resumen.visitas],
                ["visitantes", data.resumen.visitantes],
                ["producto", data.resumen.producto],
                ["noticia", data.resumen.noticia],
                ["descarga", data.resumen.descarga],
                ["peticiones", data.resumen.peticiones],
                ["contactos", data.resumen.contactos],
                ["impresiones", data.resumen.impresiones],
              ] as const
            ).map(([key, value]) => (
              <div key={key} className="bg-card px-3.5 py-3">
                <div className="text-lg font-semibold tabular-nums tracking-tight">
                  {fmt(value)}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t(`kpi.${key}`)}
                </div>
              </div>
            ))}
          </div>

          <MonthlyVisits
            points={data.diarias}
            locale={locale}
            title={t("by_month")}
          />

          <div className="grid gap-4 md:grid-cols-2">
            <TopList
              title={t("top_products")}
              items={data.productos}
              empty={t("empty")}
            />
            <TopList
              title={t("top_news")}
              items={data.noticias}
              empty={t("empty")}
            />
            <TopList
              title={t("top_downloads")}
              items={data.descargas}
              empty={t("empty")}
            />

            <Block
              title={t("published_news", {
                smart_count: data.noticiasPublicadas.total,
              })}
            >
              {data.noticiasPublicadas.ultimas.length === 0 ? (
                <p className="px-3 py-2.5 text-sm text-muted-foreground">
                  {t("no_news")}
                </p>
              ) : (
                data.noticiasPublicadas.ultimas.map((n) => (
                  <a
                    key={n.url}
                    href={n.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex gap-3 border-t border-secondary px-3 py-2 text-sm text-foreground no-underline first:border-t-0 hover:bg-secondary/60"
                  >
                    <span className="w-20 shrink-0 text-xs text-muted-foreground">
                      {date(n.fecha)}
                    </span>
                    <span className="line-clamp-2">{n.titular}</span>
                  </a>
                ))
              )}
            </Block>
          </div>

          <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
            <Block
              title={t("requests_by_profile", {
                smart_count: data.peticiones.total,
              })}
            >
              {data.peticiones.perfiles.length === 0 ? (
                <p className="px-3 py-2.5 text-sm text-muted-foreground">
                  {t("no_requests")}
                </p>
              ) : (
                data.peticiones.perfiles.slice(0, 8).map((p) => (
                  <div key={p.perfil} className="px-3 py-1.5">
                    <div className="flex justify-between text-[13px]">
                      <span className="truncate">{p.perfil}</span>
                      <span className="tabular-nums text-muted-foreground">
                        {p.peticiones}
                      </span>
                    </div>
                    <div className="mt-1 h-1.5 rounded-full bg-secondary">
                      <div
                        className="h-1.5 rounded-full bg-primary"
                        style={{ width: `${Math.max(p.pct, 2)}%` }}
                      />
                    </div>
                  </div>
                ))
              )}
            </Block>
            <Block title={t("latest_requests")}>
              {data.peticiones.ultimas.length === 0 ? (
                <p className="px-3 py-2.5 text-sm text-muted-foreground">
                  {t("no_requests")}
                </p>
              ) : (
                data.peticiones.ultimas.map((r, i) => (
                  <div
                    key={`${r.fecha}-${i}`}
                    className="flex items-center gap-3 border-t border-secondary px-3 py-2 text-[13px] first:border-t-0"
                  >
                    <span className="w-20 shrink-0 text-xs text-muted-foreground">
                      {date(r.fecha)}
                    </span>
                    <span className="min-w-0 flex-1 truncate">
                      {[r.perfil, r.provincia || r.pais, r.producto]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                    <StatePill
                      tone={
                        r.respondida
                          ? "bg-good-tint text-good"
                          : "bg-warn-tint text-warn"
                      }
                    >
                      {r.respondida ? t("answered") : t("unanswered")}
                    </StatePill>
                  </div>
                ))
              )}
            </Block>
          </div>
          <p className="text-xs text-muted-foreground">{t("privacy")}</p>
        </>
      )}
    </div>
  );
};

const Block = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <section className="rounded-xl border border-border bg-card">
    <h3 className="border-b border-secondary px-3 py-2 text-[13px] font-semibold">
      {title}
    </h3>
    <div>{children}</div>
  </section>
);

const TopList = ({
  title,
  items,
  empty,
}: {
  title: string;
  items: TopItem[];
  empty: string;
}) => (
  <Block title={title}>
    {items.length === 0 ? (
      <p className="px-3 py-2.5 text-sm text-muted-foreground">{empty}</p>
    ) : (
      items.slice(0, 6).map((item) => {
        const label = (
          <>
            <span className="min-w-0 flex-1 truncate">{item.nombre}</span>
            <span className="tabular-nums text-muted-foreground">
              {fmt(item.visitas)}
            </span>
          </>
        );
        const cls =
          "flex items-center gap-3 border-t border-secondary px-3 py-1.5 text-[13px] text-foreground no-underline first:border-t-0";
        return item.url ? (
          <a
            key={item.url + item.nombre}
            href={item.url.startsWith("http") ? item.url : `${WEB}${item.url}`}
            target="_blank"
            rel="noopener noreferrer"
            className={`${cls} hover:bg-secondary/60`}
          >
            {label}
          </a>
        ) : (
          <div key={item.nombre} className={cls}>
            {label}
          </div>
        );
      })
    )}
  </Block>
);

/** Visits per month: one series, so no legend; the exact value is in the
 *  tooltip of each bar and the highest month is labelled. */
const MonthlyVisits = ({
  points,
  locale,
  title,
}: {
  points: { fecha: string; visitas: number }[];
  locale: string;
  title: string;
}) => {
  const byMonth = new Map<string, number>();
  for (const p of points) {
    const month = p.fecha.slice(0, 7);
    byMonth.set(month, (byMonth.get(month) ?? 0) + p.visitas);
  }
  const months = [...byMonth.entries()].sort(([a], [b]) => a.localeCompare(b));
  if (months.length < 2) return null;
  const max = Math.max(...months.map(([, v]) => v), 1);
  const label = (month: string) =>
    new Date(`${month}-01T12:00:00`).toLocaleDateString(locale, {
      month: "short",
    });

  return (
    <Block title={title}>
      <div className="px-3 pt-6 pb-2">
        <div className="flex h-36 items-end gap-0.5 border-b border-border">
          {months.map(([month, visits]) => (
            <div
              key={month}
              className="group relative flex h-full flex-1 items-end"
              title={`${label(month)} ${month.slice(0, 4)}: ${nf.format(visits)}`}
            >
              {visits === max ? (
                <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[11px] font-medium tabular-nums text-foreground">
                  {fmt(visits)}
                </span>
              ) : null}
              <div
                className="w-full rounded-t-[4px] bg-primary/85 group-hover:bg-primary"
                style={{ height: `${Math.max((visits / max) * 100, 1)}%` }}
              />
            </div>
          ))}
        </div>
        <div className="mt-1 flex gap-0.5">
          {months.map(([month]) => (
            <span
              key={month}
              className="flex-1 truncate text-center text-[10.5px] text-muted-foreground"
            >
              {label(month)}
            </span>
          ))}
        </div>
      </div>
    </Block>
  );
};
